/**
 * International exam vocabulary list state.
 * The exam screen can be restored from session storage without the article
 * list, which lives only in memory. A slow response must not replace a newer
 * selection, and a failed request must not look like an empty catalog.
 */

export const EXAM_CATALOG_TIMEOUT_MS = 20000;

/**
 * Gives up when the server has not started responding within timeoutMs, so a hung request ends on
 * the error screen instead of an endless spinner. Slow body downloads are not cut off.
 * Rejects with an Error named 'TimeoutError'.
 */
export async function fetchExamCatalog(
  url,
  init = {},
  { timeoutMs = EXAM_CATALOG_TIMEOUT_MS, fetchImpl = (...args) => fetch(...args) } = {}
) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timedOut = false;
  const timer = controller
    ? setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, timeoutMs)
    : null;
  try {
    return await fetchImpl(url, controller ? { ...init, signal: controller.signal } : init);
  } catch (error) {
    if (!timedOut) throw error;
    const timeout = new Error(`Exam catalog request timed out after ${Math.round(timeoutMs / 1000)}s`);
    timeout.name = 'TimeoutError';
    throw timeout;
  } finally {
    clearTimeout(timer);
  }
}

export function beginExamLoad(previousKey) {
  const current = Number(previousKey);
  return (Number.isFinite(current) ? current : 0) + 1;
}

export function shouldApplyExamLoad(requestKey, currentKey) {
  return requestKey === currentKey;
}

export function yearNameForExamTab(examLevels, examLevelId, yearLevelKey) {
  const levels = Array.isArray(examLevels) ? examLevels : [];
  const level = levels.find((item) => item.id === examLevelId) || levels[0] || null;
  if (!level) {
    return {
      examLevelId: examLevelId || 'ielts',
      yearLevelKey: yearLevelKey || 'ielts-1',
      yearName: null
    };
  }
  const rawIndex = Number(String(yearLevelKey || '').split('-').pop());
  const index = Number.isFinite(rawIndex) && rawIndex >= 1 ? rawIndex - 1 : 0;
  const safeIndex = index < (level.years || []).length ? index : 0;
  return {
    examLevelId: level.id,
    yearLevelKey: `${level.id}-${safeIndex + 1}`,
    yearName: level.years?.[safeIndex] ?? null
  };
}

/**
 * Articles on screen always win. Otherwise loading, error, and a real empty
 * catalog are different screens.
 */
export function examCatalogView({ status, articleCount }) {
  const count = Number(articleCount) || 0;
  if (count > 0) return 'ready';
  if (status === 'loading') return 'loading';
  if (status === 'error') return 'error';
  return 'empty';
}

export function catalogFromResponse({ ok, body }) {
  if (!ok) {
    return { status: 'error', entitled: false, articles: [] };
  }
  const articles = body && Array.isArray(body.articles) ? body.articles : [];
  return {
    status: articles.length > 0 ? 'ready' : 'empty',
    entitled: !!(body && body.entitled),
    articles
  };
}
